import { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { ZoomIn, ZoomOut, RotateCw, Move, Check, X, Crop, Upload } from "lucide-react";

// Helper function to create centered crop
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export default function ImageCropEditor({
  open,
  onClose,
  imageFile,
  onSave,
  onSaveOriginal, // New prop for saving without crop
  aspectRatio = 16 / 9,
  imageType = "cover",
  title = "Ajustar Imagem"
}) {
  const [imgSrc, setImgSrc] = useState("");
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState(aspectRatio);
  const imgRef = useRef(null);
  const [saving, setSaving] = useState(false);

  // Load image when file changes
  useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImgSrc(reader.result?.toString() || "");
      });
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setImgSrc("");
      setCrop(undefined);
      setCompletedCrop(undefined);
      setScale(1);
      setRotate(0);
      setAspect(aspectRatio);
    }
  }, [open, aspectRatio]);

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    const initialCrop = centerAspectCrop(width, height, aspect);
    setCrop(initialCrop);
    setCompletedCrop(initialCrop);
  }, [aspect]);

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleRotate = () => {
    setRotate(prev => (prev + 90) % 360);
  };

  const toggleAspect = () => {
    if (imageType === "cover") {
      // Para capa: alternar entre 16:9 e livre
      if (aspect) {
        setAspect(undefined);
      } else {
        setAspect(16 / 9);
      }
    } else {
      // Para galeria: alternar entre 1:1, 4:3 e livre
      if (aspect === 1) {
        setAspect(4 / 3);
      } else if (aspect === 4 / 3) {
        setAspect(undefined);
      } else {
        setAspect(1);
      }
    }
  };

  // Simplified and robust crop function
  const getCroppedImg = useCallback(async () => {
    if (!imgRef.current || !completedCrop) {
      throw new Error("Crop not complete");
    }

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No 2d context");
    }

    // Get the natural dimensions
    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;
    
    // Calculate scale factors
    const scaleX = naturalWidth / image.width;
    const scaleY = naturalHeight / image.height;

    // Calculate crop in pixels (the crop is in display pixels, need to convert to natural)
    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;
    const cropWidth = completedCrop.width * scaleX;
    const cropHeight = completedCrop.height * scaleY;

    // For cover images, ensure minimum dimensions for quality
    let outputWidth = cropWidth;
    let outputHeight = cropHeight;
    
    if (imageType === "cover") {
      // Minimum 1920px width for cover images
      const minWidth = 1920;
      if (outputWidth < minWidth && cropWidth > 0) {
        const ratio = minWidth / outputWidth;
        outputWidth = minWidth;
        outputHeight = outputHeight * ratio;
      }
    }

    // Set canvas size
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    // Fill with white background first (for transparent images)
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Apply transformations
    ctx.save();
    
    // Move to center for rotation
    ctx.translate(canvas.width / 2, canvas.height / 2);
    
    // Apply rotation
    ctx.rotate((rotate * Math.PI) / 180);
    
    // Apply scale
    ctx.scale(scale, scale);
    
    // Move back
    ctx.translate(-canvas.width / 2, -canvas.height / 2);

    // Draw the cropped portion
    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );

    ctx.restore();

    // Convert to blob
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        0.92
      );
    });
  }, [completedCrop, rotate, scale, imageType]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const croppedBlob = await getCroppedImg();
      const croppedFile = new File([croppedBlob], imageFile?.name || "cropped.jpg", {
        type: "image/jpeg",
      });
      await onSave(croppedFile);
      onClose();
    } catch (error) {
      console.error("Error cropping image:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOriginal = async () => {
    setSaving(true);
    try {
      if (onSaveOriginal) {
        await onSaveOriginal(imageFile);
      } else {
        await onSave(imageFile);
      }
      onClose();
    } catch (error) {
      console.error("Error saving original:", error);
    } finally {
      setSaving(false);
    }
  };

  const getAspectLabel = () => {
    if (!aspect) return "Livre";
    if (aspect === 16 / 9) return "16:9";
    if (aspect === 4 / 3) return "4:3";
    if (aspect === 1) return "1:1";
    return `${aspectRatio}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-[#6E0F1A]">{title}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto py-4">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-4 bg-[#FAFAFA] p-3 rounded-xl">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomOut}
                className="border-[#F3C9CF] text-[#6E0F1A]"
                title="Diminuir Zoom"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <div className="w-32">
                <Slider
                  value={[scale * 100]}
                  min={50}
                  max={300}
                  step={5}
                  onValueChange={(v) => setScale(v[0] / 100)}
                  className="cursor-pointer"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomIn}
                className="border-[#F3C9CF] text-[#6E0F1A]"
                title="Aumentar Zoom"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
              <span className="text-sm text-[#6E0F1A]/60 min-w-[50px]">{Math.round(scale * 100)}%</span>
            </div>

            <div className="h-6 w-px bg-[#F3C9CF]" />

            <Button
              variant="outline"
              size="sm"
              onClick={handleRotate}
              className="border-[#F3C9CF] text-[#6E0F1A]"
              title="Rotacionar 90°"
            >
              <RotateCw className="w-4 h-4 mr-2" />
              Girar
            </Button>

            <div className="h-6 w-px bg-[#F3C9CF]" />

            <Button
              variant="outline"
              size="sm"
              onClick={toggleAspect}
              className="border-[#F3C9CF] text-[#6E0F1A]"
              title="Alterar proporção"
            >
              <Crop className="w-4 h-4 mr-2" />
              {getAspectLabel()}
            </Button>
          </div>

          {/* Image Editor Area */}
          <div className="flex items-center justify-center bg-[#1a1a1a] rounded-xl p-4 min-h-[400px]">
            {imgSrc && (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                className="max-h-[500px]"
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imgSrc}
                  style={{
                    transform: `scale(${scale}) rotate(${rotate}deg)`,
                    maxHeight: "500px",
                    maxWidth: "100%",
                    transformOrigin: "center center",
                  }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            )}
          </div>

          {/* Instructions */}
          <div className="mt-4 text-center space-y-2">
            <div className="flex items-center justify-center gap-2 text-sm text-[#6E0F1A]/60">
              <Move className="w-4 h-4" />
              <span>Arraste os cantos para ajustar a área de corte</span>
            </div>
            {imageType === "cover" && (
              <p className="text-xs text-[#6E0F1A]/40">
                Dica: Para fotos de capa, use a proporção 16:9 (horizontal) para melhor visualização
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex gap-3 pt-4 border-t border-[#F3C9CF]/30">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-[#6E0F1A] text-[#6E0F1A] rounded-full"
          >
            <X className="w-4 h-4 mr-2" />
            Cancelar
          </Button>
          <Button
            variant="outline"
            onClick={handleSaveOriginal}
            disabled={saving}
            className="border-[#567D50] text-[#567D50] rounded-full"
            title="Enviar a imagem original sem cortes"
          >
            <Upload className="w-4 h-4 mr-2" />
            Enviar Original
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !completedCrop}
            className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full"
          >
            <Check className="w-4 h-4 mr-2" />
            {saving ? "Salvando..." : "Aplicar Corte"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
